import { useContext, useEffect, useState } from 'react';
import axios from "axios";
import { navigate } from "raviger";
import { Form, Formik, useFormikContext } from 'formik';
import {
  Button,
  ButtonGroup,
  Card,
  Col,
  FormGroup,
  Form as BootstrapForm,
  Row,
} from 'react-bootstrap';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ButtonSpinner from '../components/ButtonSpinner.js';
import {
  faArrowAltCircleLeft,
} from '@fortawesome/free-solid-svg-icons';
import * as Yup from 'yup';
import { Checkbox, DateTimePicker, DropDown, TextInput } from '../components/Form';
import { SystemErrorContext } from '../components/SystemError';
import { AuthContext } from "../accounts/AccountsReducer";
import Patient from './components/Patient';

// Child component to handle the useEffect logic
const AutoSubmitEffect = (props) => {
  const { values, submitForm } = useFormikContext();

  useEffect(() => {
    // Add your condition to trigger submission
    if (props.submit_form) {
      submitForm();
    }
  }, [props.submit_form, submitForm]);

  return null;
};

const TreatmetRequestForm = (props) => {

  const { setShowSystemError } = useContext(SystemErrorContext);
  const { state } = useContext(AuthContext);

  const [data, setData] = useState({
    medical_record: null,
    suggested_admin_time: '',
    actual_admin_time: '',
    assignee: null,
    not_administered: false,
    notes: '',
    animal_object:{id:'', name:''},
    treatment_object:{description:''},
    exams:[]
  })

  const [assigneeChoices, setAssigneeChoices] = useState([]);
  const [treatmentChoices, setTreatmentChoices] = useState([]);

  useEffect(() => {
    let unmounted = false;
    let source = axios.CancelToken.source();

    const fetchTreatments = async () => {
      // Fetch Treatment data.
      await axios.get('/vet/api/treatment/', {
        cancelToken: source.token,
      })
      .then(response => {
        if (!unmounted) {
          let treatment_options = [];
          response.data.forEach(function(treatment) {
            treatment_options.push({value: treatment.id, label: treatment.description, category:treatment.category, unit:treatment.unit, routes:treatment.routes});
          });
          setTreatmentChoices(treatment_options);
        }
      })
      .catch(error => {
        setShowSystemError(true);
      });
    };
    fetchTreatments();

    const fetchTreatmentRequest = async () => {
      // Fetch TreatmentRequest data.
      await axios.get('/vet/api/treatmentrequest/' + props.id + '/?incident=' + state.incident.id, {
        cancelToken: source.token,
      })
      .then(response => {
        if (!unmounted) {
          response.data['exams'] = [];
          response.data['actual_admin_time'] = response.data.not_administered ? null : response.data['actual_admin_time'] || null
          setData(response.data);
        }
      })
      .catch(error => {
        setShowSystemError(true);
      });
    };
    if (props.id) {
      fetchTreatmentRequest();
    }

    const fetchAssignees = async () => {
      // Fetch assignee data.
      await axios.get('/accounts/api/user/?vet_techs=true&organization=' + state.organization.id, {
        cancelToken: source.token,
      })
      .then(response => {
        if (!unmounted) {
          let options = [];
          response.data.forEach(function(person) {
            options.unshift({value: person.id, label: person.first_name + ' ' + person.last_name})
          });
          setAssigneeChoices(options);
        }
      })
      .catch(error => {
        setShowSystemError(true);
      });
    };
    fetchAssignees();

    // Cleanup.
    return () => {
      unmounted = true;
      source.cancel();
    };
  }, [props.id]);

  return (
    <Formik
      initialValues={data}
      enableReinitialize={true}
      validationSchema={Yup.object({
        assignee: Yup.number().nullable(),
        suggested_admin_time: Yup.string().required('Required'),
        actual_admin_time: Yup.string().nullable(),
        notes: Yup.string().nullable(),
        quantity: Yup.number().positive('Must be positive').required('Required'),
        route: Yup.string().nullable(),
      })}
      onSubmit={(values, { setSubmitting }) => {
        if (props.id) {
          setSubmitting(true);
          if (!values.actual_admin_time) {
            values['actual_admin_time'] = null;
          }
          axios.put('/vet/api/treatmentrequest/' + props.id + '/', values)
          .then(response => {
            setSubmitting(true);
            if (props.modal && props.setParentData) {
              props.setParentData(prevItems =>
                prevItems.map(item => {
                  if (Number(item.object_id) === Number(props.id)) {
                    // Return a new object with updated properties
                    return { ...item, itemProps: {
                      style: {
                        background: response.data.not_administered ? 'gray' : response.data.actual_admin_time ? '#008000' : dayjs(response.data.suggested_admin_time).isBefore(dayjs()) ? '#ff4c4c' : '#2196f3',  // Background color
                        color: 'white',       // Text color
                        borderColor: 'black'
                      }
                    }};
                  }
                  // Return unchanged items as they are
                  return item;
                })
              );
              props.setResetSubmit(false);
              props.setShowModal(false);
            }
            else {
              navigate('/' + props.organization + '/' + props.incident + '/vet/treatment/' + data.treatment_plan);
            }
          })
          .catch(error => {
            setShowSystemError(true);
            setSubmitting(true);
          });
        }
      }}
    >
      {formikProps => (
        <Card border="secondary" className={props.modal ? "" : "mt-5"}>
          {!props.modal ? <Card.Header as="h5" className="ps-3">
            {state.prevLocation ?
              <span style={{ cursor: 'pointer' }} onClick={() => navigate(state.prevLocation)} className="me-3"><FontAwesomeIcon icon={faArrowAltCircleLeft} size="lg" inverse /></span>
            :
              <span style={{ cursor: 'pointer' }} onClick={() => navigate('/' + props.organization + "/" + props.incident + "/vet/treatment/" + data.treatment_plan)} className="me-3"><FontAwesomeIcon icon={faArrowAltCircleLeft} size="lg" inverse /></span>
            }
            Treatment Form - {data.treatment_object.description}
          </Card.Header> : ""}
          {!props.modal ? <Patient animal={data.animal_object} organization={props.organization} incident={props.incident} medical_plan={props.state && props.state.steps.exam.medical_plan || data.exams.filter(exam => (exam.medical_plan)).length ? data.exams.filter(exam => (exam.medical_plan))[0].medical_plan : ''} /> : ""}
          <Card.Body>
            <Form>
              <FormGroup>
                <Row className="gx-2">
                  <TextInput
                    id="quantity"
                    name="quantity"
                    type="text"
                    xs={props.modal ? "3" : "2"}
                    label="Quantity"
                  />
                  {/* <Col xs={"1"}>
                    <DropDown
                      label="Unit"
                      id="unitDropdown"
                      name="unit"
                      type="text"
                      key={`my_unique_unit_select_key__${formikProps.values.unit}`}
                      options={treatmentChoices.length > 0 && formikProps.values.treatment ? treatmentChoices.filter(choice => choice.value === formikProps.values.treatment).map(choice => ({'value':choice.unit, 'label':choice.unit})) : [{'value':'', 'label':''}]}
                      value={formikProps.values.unit||data.unit}//||treatmentChoices.filter(choice => choice.value === formikProps.values.treatment)[0].unit}
                      isClearable={false}
                      onChange={(instance) => {
                        formikProps.setFieldValue("unit", instance === null ? '' : instance.value);
                      }}
                      placeholder=""
                      disabled={true}
                    />
                  </Col> */}
                  <Col xs={props.modal ? "3" : "2"}>
                    <DropDown
                      label="Route"
                      id="routeDropdown"
                      name="route"
                      type="text"
                      key={`my_unique_route_select_key__${formikProps.values.route}`}
                      options={treatmentChoices.length > 0 && formikProps.values.treatment && treatmentChoices.filter(choice => Number(choice.value) === Number(formikProps.values.treatment))[0].routes.length > 0 ? treatmentChoices.filter(choice => Number(choice.value) === Number(formikProps.values.treatment))[0].routes.map(route => ({'value':route, 'label':route})) : [{'value':'', 'label':''}]}
                      value={formikProps.values.route||data.route}
                      isClearable={false}
                      onChange={(instance) => {
                        formikProps.setFieldValue("route", instance === null ? '' : instance.value);
                      }}
                    />
                  </Col>
                </Row>
                <Row className="mt-3">
                  <Col xs={props.modal ? "8" : "6"}>
                    <DropDown
                      label="Administrator"
                      id="assigneeDropdown"
                      name="assignee"
                      type="text"
                      key={`my_unique_assignee_select_key__${formikProps.values.assignee}`}
                      options={assigneeChoices}
                      isClearable={true}
                      onChange={(instance) => {
                        formikProps.setFieldValue("assignee", instance === null ? '' : instance.value);
                      }}
                      disabled={formikProps.values.not_administered}
                    />
                  </Col>
                </Row>
                <Row className="mt-3 gx-2 ps-0 me-3">
                  <DateTimePicker
                    label="Suggested Admin Time"
                    name="suggested_admin_time"
                    id="suggested_admin_time"
                    xs={props.modal ? "6" : "4"}
                    onChange={(date, dateStr) => {
                      formikProps.setFieldValue("suggested_admin_time", dateStr)
                    }}
                    setFieldValue={formikProps.setFieldValue}
                    value={formikProps.values.suggested_admin_time||null}
                    disabled={true}
                  />
                  <DateTimePicker
                    label="Actual Admin Time"
                    name="actual_admin_time"
                    id="actual_admin_time"
                    xs={props.modal ? "6" : "4"}
                    clearable={true}
                    now={true}
                    onChange={(date, dateStr) => {
                      formikProps.setFieldValue("actual_admin_time", dateStr)
                    }}
                    value={formikProps.values.actual_admin_time||null}
                    disabled={formikProps.values.not_administered}
                    setFieldValue={formikProps.setFieldValue}
                  />
                </Row>
                <Row className="mt-3 gx-2">
                  <TextInput
                    as="textarea"
                    label="Notes"
                    name="notes"
                    id="notes"
                    xs={props.modal ? "12" : "8"}
                    rows={4}
                    value={formikProps.values.notes || ''}
                  />
                </Row>
                {/* <BootstrapForm.Label className="mt-3">Not Administered</BootstrapForm.Label> */}
                <Row className="mb-0 ms-0 mt-3 float-start p-0">
                  <Col className="p-0">
                    <Checkbox
                      id="not_administered"
                      name={"not_administered"}
                      top_label="Not Administered"
                      checked={formikProps.values.not_administered || false}
                      onChange={() => {
                        formikProps.setFieldValue("not_administered", !formikProps.values.not_administered);
                        formikProps.setFieldValue("assignee", null);
                        formikProps.setFieldValue("actual_admin_time", null);
                      }}
                      style={{
                        transform:"scale(2.0)",
                        marginLeft:"0px",
                        marginBottom:"-5px",
                        marginTop:"0px"
                      }}
                    />
                  </Col>
                </Row>
              </FormGroup>
            </Form>
          </Card.Body>
          {!props.modal ? <ButtonGroup>
            <ButtonSpinner isSubmitting={formikProps.isSubmitting} isSubmittingText="Saving..." className="btn btn-primary" onClick={() => { formikProps.submitForm() }}>{formikProps.values.assignee ? "Complete" : "Save"}</ButtonSpinner>
          </ButtonGroup> : ""}
          <AutoSubmitEffect submit_form={props.submit_form} />
        </Card>
      )}
    </Formik>
  );
};

export default TreatmetRequestForm;
